import { Controller } from '@hotwired/stimulus';

// The index placeholder inside the <template> - the same literal is in _nested_rows.html.erb.
const NEW_RECORD = /NEW_RECORD/g;

// What counts as somebody having typed here. Deliberately not every control in the row: the
// gear row's category picker keeps one radio checked and its order field falls back to 0, so
// a blank row would look filled before it was touched, and the picker's search box submits
// nothing at all.
const TYPED_INTO = 'input[type="text"], input[type="url"], input[type="email"], textarea';

const ROW = '[data-nested-form-target="row"]';
const REMOVE = '[data-nested-form-target="remove"]';

// `type="checkbox"` is not decoration: `check_box` renders its unchecked companion first, so
// without it this picks up a hidden input whose value is "0" and ticking it does nothing.
const DESTROY = 'input[type="checkbox"][name$="[_destroy]"]';

// A repeating set of `fields_for` rows that grows as it is filled in, so nothing has to be
// saved before the next item can be typed - "always one empty row at the bottom".
//
// Knows nothing about what it repeats: the links editor, the gear editor (#118) and the
// price list (#121) all wire it the same way, from profile_edits/_nested_rows.html.erb.
//
//   <div data-controller="nested-form" data-action="input->nested-form#append">
//     <div data-nested-form-target="rows">...rows, each data-nested-form-target="row"...</div>
//     <template data-nested-form-target="template">...one blank row, index NEW_RECORD...</template>
//
// Progressive enhancement: the server renders the trailing blank row itself and every saved
// row carries a `_destroy` checkbox, so with no JS the editor is what it was before this
// existed - one new item per save, removal on submit. This only makes both immediate.
export default class extends Controller {
  static targets = ['rows', 'row', 'template', 'fallback', 'remove'];

  // Swapped as each row connects rather than in `connect()`: rows appended later have to go
  // through the same swap, and these callbacks fire for them too.
  removeTargetConnected(button) {
    this.refreshRemove(button.closest(ROW));
  }

  fallbackTargetConnected(control) {
    control.classList.add('hidden');
  }

  append(event) {
    const row = event.target.closest(ROW);
    if (!row) return;

    this.promote(row);
    this.refreshRemove(row);
    this.ensureBlankRow();
  }

  remove(event) {
    const row = event.target.closest(ROW);
    if (!row) return;

    // A saved row has to stay in the form and say what became of it; one the server has
    // never heard of can simply go.
    const destroy = row.querySelector(DESTROY);
    if (destroy) {
      destroy.checked = true;
      row.classList.add('hidden');
    } else {
      row.remove();
    }

    this.ensureBlankRow();
  }

  // There is always somewhere to type the next item: the row just typed into stops being the
  // empty one, and a removed row can have been it.
  ensureBlankRow() {
    const blank = this.blankRow;
    if (!blank) this.appendBlankRow();
    else if (blank !== this.rowsTarget.lastElementChild) this.rowsTarget.append(blank);
  }

  appendBlankRow() {
    const html = this.templateTarget.innerHTML.replace(NEW_RECORD, this.nextIndex());
    this.rowsTarget.insertAdjacentHTML('beforeend', html);
  }

  // A row offers to be removed once there is something to remove: a saved one always, an
  // unsaved one from the moment it is typed into. On the blank row at the bottom the control
  // would either do nothing or take away the only place left to type.
  refreshRemove(row) {
    const button = row?.querySelector(REMOVE);
    if (!button) return;

    button.classList.toggle('hidden', !row.querySelector(DESTROY) && !this.filled(row));
  }

  // Only has to be unique among the rows on this page - Rails treats the index as a grouping
  // key, not as an id. `Date.now()` on its own repeats when two rows are added inside the
  // same millisecond, which a removal followed by a keystroke can manage.
  nextIndex() {
    this.index = Math.max(Date.now(), (this.index || 0) + 1);
    return this.index;
  }

  filled(row) {
    return Array.from(row.querySelectorAll(TYPED_INTO)).some((input) => input.value.trim() !== '');
  }

  // The server marks the one pristine row instead of asking the controller to infer intent from
  // empty controls. On the first typed value it becomes an ordinary row; sortable then reveals
  // its handle and the template provides the next pinned blank at the bottom.
  promote(row) {
    if (!row.hasAttribute('data-nested-form-pristine') || !this.filled(row)) return;

    row.removeAttribute('data-nested-form-pristine');
    this.dispatch('promoted', { detail: { row } });
  }

  get blankRow() {
    return (
      this.rowTargets.find((row) => row.hasAttribute('data-nested-form-pristine')) ||
      this.rowTargets.find(
        (row) =>
          !row.classList.contains('hidden') && !row.querySelector(DESTROY) && !this.filled(row),
      )
    );
  }
}
